/**
 * Sparkline — minimal inline SVG line chart for trend hints in tables and
 * KPI rows. No charting library — keeps the dependency surface small.
 */
import { cn } from './cn';

export interface SparklineProps {
  data: readonly number[];
  /** Stroke colour; defaults to slate-600 (token-driven via Tailwind). */
  color?: string;
  width?: number;
  height?: number;
  className?: string;
  /** Optional NL aria-label, e.g. "Volume laatste 7 dagen". */
  ariaLabel?: string;
}

export function Sparkline({
  data,
  color = 'var(--color-slate-600)',
  width = 80,
  height = 20,
  className,
  ariaLabel,
}: SparklineProps): JSX.Element | null {
  if (data.length === 0) {
    return null;
  }
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const denom = data.length - 1 || 1;
  const points = data
    .map((v, i) => {
      const x = (i / denom) * width;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
